// Maps the issue endpoint's outcome to operator-facing copy. The endpoint answers with a
// problem-details body, so `code` is the discriminator where one is present and the status
// is the fallback — two different 409s mean opposite things to the operator.
// The `duplicate-issue-window` 409: the backend read a COMMITTED card row for this guest
// at this location inside DUPLICATE_ISSUE_WINDOW_SECONDS = 180. A card certainly exists,
// so this is the one "too soon" failure allowed to say so. Three minutes, not two: the
// duplicate window is the longer of the two guards, so it is the wait that clears the
// path to a second card.
// It does not invite the operator to press Send again, and the composer no longer arms
// X-Confirm-Duplicate on it, so pressing Send inside the window can only earn this same
// 409 — no money moves. Both halves had to change together: the copy used to say "press
// Send again to confirm", which made the bypass a choice the operator made. Dropping the
// line while keeping the arming would have made it a confirmation nobody gave, and the
// risk is not symmetric. Confirming bypasses this guard and lands on `rateLimiter.claim`,
// keyed userId + locationId + guestPhoneHash while this guard is keyed location + guest.
// The operator who made the first card still holds their claim
// (ISSUE_RATE_LIMIT_WINDOW_SECONDS = 120, never released) and would only earn a 429; a
// second operator holds none and would fund a real second card on the spot.
const GIFT_CARD_DUPLICATE_WINDOW =
  'A gift card was already created for this guest in the last 3 minutes. Check the timeline — you can create another one once 3 minutes have passed.'

// The 429 rate-limit rejection. NOT the same statement as the 409 above.
// `rateLimiter.claim` runs before fieldEncryption.ensureReady(), shift4Client.issue and
// repository.save, and is never released (fail closed). So a 429 means only that an
// issuance for this guest was ATTEMPTED inside ISSUE_RATE_LIMIT_WINDOW_SECONDS = 120 — it
// also covers a first attempt still in flight, and one that failed after the claim
// (Shift4 decline, timeout, gateway 504, encryption fault), where no card exists at all.
// Claiming a card was created here is how an operator walks away from a guest who got
// nothing, so the wording stays indeterminate. The wait is still three minutes: if the
// first attempt did succeed, the 180s duplicate guard outlives this one.
// "Attempted" carries that indeterminacy on its own, without the "Card status unknown"
// prefix the other two indeterminate messages use: the common case by far is a card that
// exists and is already rendered in the timeline, and opening with "status unknown" puts
// an alarm on the usual outcome.
const GIFT_CARD_RATE_LIMITED =
  'A gift card for this guest was just attempted. Check the timeline; you can try again once 3 minutes have passed.'

// The backend's `duplicate-issue-window` 409: a live card already exists for this guest
// at this location. Nothing in this app calls it now that the composer no longer arms a
// confirmation on this code; kept so the module stays word-for-word with urf's copy, and
// so deleting it does not pre-empt the open decision on whether the flow returns.
export function isDuplicateIssueWindowError(error) {
  return (error?.body?.code ?? error?.data?.code) === 'duplicate-issue-window'
}

export function giftCardErrorMessage(error) {
  const code = error?.body?.code ?? error?.data?.code

  // A key reused with different values. The ORIGINAL request is untouched and this one
  // was never attempted, so the safe instruction is the opposite of the generic 409's.
  if (code === 'idempotency-key-reuse') {
    return 'Gift card not created: this request was already used for a different card. Reopen the gift card form and try again.'
  }

  // A committed card row exists for this guest. The only "too soon" failure that can
  // state that as fact — the 429 below cannot.
  if (code === 'duplicate-issue-window') {
    return GIFT_CARD_DUPLICATE_WINDOW
  }

  switch (error?.status) {
    case 400:
      return 'Gift card not created: the amount or the guest’s contact details were rejected.'
    case 401:
      return 'Gift card not created: your session expired. Refresh and try again.'
    case 403:
      return 'Gift card not created: gift cards are not enabled for this location, or you do not have permission.'
    case 409:
      return 'Card status unknown — an identical request is already in progress. Check the timeline before trying again.'
    case 429:
      // Deliberately NOT the duplicate 409's copy: the claim is taken before anything
      // that funds a card, so this cannot assert one exists. Indeterminate, same wait.
      return GIFT_CARD_RATE_LIMITED
    default:
      return 'Card status unknown — the request may or may not have gone through. Check the timeline before trying again.'
  }
}

// Shown when the card was issued but the reply that was meant to follow it failed.
// A retry after this point must not re-issue the card, only resend the message.
export function giftCardIssuedReplyFailedMessage() {
  return 'The gift card was created, but the message failed to send. Resend the message — the card will not be issued again.'
}
